import React, { useRef, useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Hidden } from "@mui/material";
import { useTranslation } from "react-i18next";
import Menu from "../menu/Menu";
import UserMenu from "../menu/UserMenu";
import {
  SidebarDrawer,
  StyledImage,
  StyledLogoMini,
  StyledLogoDefault,
  SidebarLogoContainer,
  SidebarWrapperContainer
} from "./LayoutStyles";
import defaultMiniLogo from "assets/img/miniLogo.png";
import defaultLogo from "assets/img/defaultLogo.png";
import { getApplicationLogo } from "utils/theme";

// We've created this component so we can have a ref to the wrapper of the links that appears in our sidebar.
// This was necessary so that we could initialize PerfectScrollbar on the links.
// There might be something with the Hidden component from material-ui, and we didn't have access to
// the links, and couldn't initialize the plugin.
const SidebarWrapper = ({ miniActive, children }) => {
  const sidebarWrapperRef = useRef();

  return (
    <SidebarWrapperContainer miniActive={miniActive} withOverflowHidden ref={sidebarWrapperRef}>
      {children}
    </SidebarWrapperContainer>
  );
};

SidebarWrapper.propTypes = {
  miniActive: PropTypes.bool.isRequired,
  children: PropTypes.array.isRequired
};

const Sidebar = ({ logoText, open, miniActive, changeLanguage, handleDrawerToggle }) => {
  const { i18n } = useTranslation();

  const [logoSrc, setLogoSrc] = useState(null);

  const onError = useCallback(() => {
    const defLogo = !miniActive ? defaultLogo : defaultMiniLogo;
    setLogoSrc(defLogo);
  }, [miniActive]);

  useEffect(() => {
    const src = getApplicationLogo(!!miniActive);
    setLogoSrc(src);
  }, [miniActive]);

  const brand = useMemo(
    () => (
      <SidebarLogoContainer id="sidebar_logo">
        <StyledLogoMini href="/">{<StyledImage src={logoSrc} alt="logo" onError={onError} />}</StyledLogoMini>
        {miniActive && logoText && <StyledLogoDefault href="/">{logoText}</StyledLogoDefault>}
      </SidebarLogoContainer>
    ),
    [miniActive, logoSrc, onError, logoText]
  );

  return (
    <div>
      <Hidden mdUp>
        <SidebarDrawer
          variant="temporary"
          anchor="right"
          open={open}
          miniActive={miniActive}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
        >
          {brand}
          <SidebarWrapper miniActive={miniActive}>
            <UserMenu miniActive={miniActive} changeLanguage={changeLanguage} language={i18n.language} />
            <Menu miniActive={miniActive} />
          </SidebarWrapper>
        </SidebarDrawer>
      </Hidden>
      <Hidden mdDown>
        <SidebarDrawer anchor="left" variant="permanent" open miniActive={miniActive}>
          {brand}
          <SidebarWrapper miniActive={miniActive}>
            <UserMenu miniActive={miniActive} changeLanguage={changeLanguage} language={i18n.language} />
            <Menu miniActive={miniActive} />
          </SidebarWrapper>
        </SidebarDrawer>
      </Hidden>
    </div>
  );
};

Sidebar.propTypes = {
  miniActive: PropTypes.bool.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
  changeLanguage: PropTypes.func.isRequired,
  logoText: PropTypes.string,
  open: PropTypes.bool
};

//router is needed for re-rendering
export default Sidebar;
